/**
 * `scan()` — D6's abstraction, made real in Phase 3 step 3.
 *
 * THE DECISION D6 DEFERRED WAS "which decoder", and the answer is BOTH, chosen at
 * runtime, because the atelier's two test devices disagree:
 *  - **Android/Chrome** ships `BarcodeDetector` natively. It is fast, costs nothing to
 *    load, and is what makes the under-two-seconds criterion reachable.
 *  - **iOS Safari does not implement it at all** (still true at the time of writing).
 *    Without a fallback, scanning simply would not exist on an iPhone — and one of the
 *    two timed criteria is an iPhone.
 *
 * THE FALLBACK IS DYNAMICALLY IMPORTED, and that is a bundle decision, not a style
 * one. ZXing is larger than this app's entire entry bundle; loading it eagerly would
 * blow the 500 kB ceiling the smoke test enforces. `import()` puts it in its own chunk
 * that only an iPhone ever downloads, and only when the user opens the scanner.
 *
 * WHAT THIS MODULE DOES NOT DO: decide what a code MEANS. It hands back a string. The
 * server normalises and resolves it (`catalog/codes.ts`), because a scanner that
 * guesses is a scanner that opens the wrong garment.
 */

/** The subset of the native API this module uses, declared rather than depended on:
 * `BarcodeDetector` is not in the DOM lib, and typing it here keeps the feature test
 * honest instead of casting `window` to `any` at the call site. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

/** QR first: it is what this app prints. The 1D formats are the garment's OWN label — a brand's
 * EAN-13, a retailer's Code 128.
 *
 * ⚠️ EAN-8, UPC-A AND UPC-E JOINED AT 0.1.515, with barcode linking. The native detector reads
 * ONLY the formats named here, while the ZXing fallback reads every format it knows — so without
 * them a small-tag EAN-8 or a US brand's UPC scanned on one phone and not on another, the kind of
 * device-dependent silence nobody can diagnose at a rack. (The UPC-A/EAN-13 leading-zero twin is
 * folded server-side, catalog/codes.ts `canonicalBarcode`.) */
const FORMATS = ['qr_code', 'code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_39'];

export type ScanEngine = 'native' | 'fallback' | 'unavailable';

function nativeDetector(): BarcodeDetectorCtor | undefined {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
  return typeof ctor === 'function' ? ctor : undefined;
}

/**
 * Which engine this device will use, WITHOUT starting a camera — so the screen can
 * warn about the download before asking for permission, and so the choice is testable
 * with a stubbed global rather than a real phone.
 */
export function scanEngine(): ScanEngine {
  if (nativeDetector()) return 'native';
  // A camera is the other half; without one, neither engine can do anything, and
  // saying so is better than a permission prompt that leads nowhere.
  const media = (globalThis as { navigator?: { mediaDevices?: unknown } }).navigator?.mediaDevices;
  return media ? 'fallback' : 'unavailable';
}

export interface ScanSession {
  /** The engine actually in use, once started — reported so the UI can be honest. */
  engine: ScanEngine;
  stop: () => void;
}

export interface ScanOptions {
  video: HTMLVideoElement;
  onCode: (code: string) => void;
  onError: (error: unknown) => void;
  /** How often to sample frames with the native detector. 250 ms is comfortably
   * inside the 2-second budget while leaving the main thread alone between frames. */
  intervalMs?: number;
}

/**
 * Start scanning into `video`. Returns a session whose `stop()` releases the camera —
 * ALWAYS call it: a live `MediaStream` keeps the phone's camera light on, which reads
 * to a user as the app spying on them.
 */
export async function startScan(options: ScanOptions): Promise<ScanSession> {
  const engine = scanEngine();
  if (engine === 'unavailable') {
    throw new Error('This device has no camera available to this browser.');
  }

  // `facingMode: environment` is the rear camera. Without it a phone opens the selfie
  // camera, which is a comic but real failure for someone pointing at a rack.
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: 'environment' } },
    audio: false,
  });
  options.video.srcObject = stream;
  options.video.setAttribute('playsinline', 'true'); // iOS: otherwise it goes fullscreen
  await options.video.play();

  let stopped = false;
  const stopStream = (): void => {
    stopped = true;
    for (const track of stream.getTracks()) track.stop();
    options.video.srcObject = null;
  };

  const Native = nativeDetector();
  if (Native) {
    const detector = new Native({ formats: FORMATS });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const tick = async (): Promise<void> => {
      if (stopped || !context) return;
      try {
        const width = options.video.videoWidth;
        const height = options.video.videoHeight;
        if (width > 0 && height > 0) {
          canvas.width = width;
          canvas.height = height;
          context.drawImage(options.video, 0, 0, width, height);
          const found = await detector.detect(canvas);
          const value = found[0]?.rawValue;
          if (value !== undefined && value !== '') {
            options.onCode(value);
            return; // one code per session; the caller decides whether to resume
          }
        }
      } catch (error) {
        // A detector throwing on a single frame is not fatal — a hand moving through
        // shot does it. Report once and keep sampling; the caller decides to stop.
        options.onError(error);
      }
      if (!stopped) setTimeout(() => void tick(), options.intervalMs ?? 250);
    };
    void tick();
    return { engine: 'native', stop: stopStream };
  }

  // FALLBACK, loaded only here — see the header. If the chunk fails to arrive (an
  // iPhone on a bad connection), that is a named failure, not a camera that silently
  // never finds anything.
  try {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const reader = new BrowserMultiFormatReader();
    const controls = await reader.decodeFromVideoElement(options.video, (result) => {
      if (stopped) return;
      const text = result?.getText();
      if (text !== undefined && text !== '') options.onCode(text);
    });
    return {
      engine: 'fallback',
      stop: () => {
        controls.stop();
        stopStream();
      },
    };
  } catch (error) {
    stopStream();
    throw error instanceof Error ? error : new Error('The barcode reader could not be loaded.');
  }
}
